import { useEffect, useState } from "react";

// STYLES
import * as Style from "./SettingsStyle";

// LIBRARIES
import { useTranslation } from "react-i18next";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

// MISC
import { IProps } from "./SettingsModel";
import { ButtonTypes, InputTypes, SnackbarTypes } from "models/GeneralTypes/generalTypes";
import { COLORS } from "constants/styleVariables";
import { IModalData } from "../../ModalAtomModel";
import { ISettingsProperties } from "models/interfaces";

// ASSETS

// REDUX
import { modalProps, toggleModalState, toggleSnackbarState } from "slices/uiSlice";
import { getSettings, updateSettings } from "api/settingsApi";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import InputAtom from "components/Atoms/InputAtom/InputAtom";

const Settings = (props: IProps) => {
  // PROPS
  const { handleClose = () => { } } = props;

  // CONSTANTS USING LIBRARIES
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modalData = useAppSelector<IModalData>(modalProps);

  // CONSTANTS USING HOOKS
  const [settingsProperties, setSettingsProperties] =
    useState<ISettingsProperties>({
      distanceFromLocker: null,
      maintenanceMode: false,
      vacationMode: false,
      badWeatherMode: false,
      badWeatherWarning: false,
      appVersionIos: "",
      appVersionAndroid: "",
      googleStoreLink: "",
      appleStoreLink: "",
      supportPhoneNumber: "",
      stripeApiKey: "",
      veriffPublicKey: "",
      veriffSecretKey: "",
      tutorialVideoUrl: ""
    });
  // GENERAL CONSTANTS

  // USE EFFECT FUNCTION
  useEffect(() => {
    dispatch(getSettings())
      .unwrap()
      .then((response) => {
        setSettingsProperties(response);
      });
    // eslint-disable-next-line
  }, []);
  // REQUEST API FUNCTIONS

  // HANDLERS FUNCTIONS
  const handleChangeSettings = (
    name: string,
    value: React.SetStateAction<any>
  ) => {
    if (name === "vacationMode" && settingsProperties.vacationMode === true) {
      setSettingsProperties({
        ...settingsProperties,
        vacationMode: false,
      });
    } else if (
      name === "vacationMode" &&
      settingsProperties.vacationMode === false
    ) {
      setSettingsProperties({
        ...settingsProperties,
        vacationMode: true,
        maintenanceMode: false,
        badWeatherMode: false,
        badWeatherWarning: false,
      });
    } else if (
      name === "maintenanceMode" &&
      settingsProperties.maintenanceMode === false
    ) {
      setSettingsProperties({
        ...settingsProperties,
        maintenanceMode: true,
        vacationMode: false,
        badWeatherMode: false,
        badWeatherWarning: false,
      });
    } else if (
      name === "maintenanceMode" &&
      settingsProperties.maintenanceMode === true
    ) {
      setSettingsProperties({
        ...settingsProperties,
        maintenanceMode: false,
      });
    } else if (
      name === "badWeatherMode" &&
      settingsProperties.badWeatherMode === false
    ) {
      setSettingsProperties({
        ...settingsProperties,
        badWeatherMode: true,
        badWeatherWarning: false,
      });
    } else if (
      name === "badWeatherMode" &&
      settingsProperties.badWeatherMode === true
    ) {
      setSettingsProperties({
        ...settingsProperties,
        badWeatherMode: false,
      });
    } else if (
      name === "badWeatherWarning" &&
      settingsProperties.badWeatherWarning === false
    ) {
      setSettingsProperties({
        ...settingsProperties,
        badWeatherWarning: true,
        badWeatherMode: false,
      });
    } else if (
      name === "badWeatherWarning" &&
      settingsProperties.badWeatherWarning === true
    ) {
      setSettingsProperties({
        ...settingsProperties,
        badWeatherWarning: false,
      });
    } else {
      setSettingsProperties({
        ...settingsProperties,
        [name]: value,
      });
    }
  };

  const handleUpdateSettings = () => {
    dispatch(updateSettings(settingsProperties))
      .then(() => {
        dispatch(toggleSnackbarState({
          isVisible: true,
          type: SnackbarTypes.SETTINGS_SUCCESS,
        }))
        dispatch(
          toggleModalState({
            ...modalData,
            isVisible: false,
            type: "",
          })
        );
      })
      .catch((error) => {
        console.log(error)
        dispatch(toggleSnackbarState({
          isVisible: true,
          type: SnackbarTypes.SUPPORT_ERROR,
        }))
      })

  };

  return (
    <Style.Container>
      <Style.CloseIconContainer>
        <Style.CloseIcon onClick={handleClose} />
      </Style.CloseIconContainer>
      <Style.Title>{t("page.settings.title")}</Style.Title>
      <Style.Subtitle>
        {t("page.settings.subtitle.SettingsOfApplication")}
      </Style.Subtitle>
      <Style.SettingsContent>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.maxDistanceSubtitle")}
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.maxDistanceSubtitle.placeholder")}
            inputValue={settingsProperties.distanceFromLocker}
            setInputValue={(value) =>
              handleChangeSettings("distanceFromLocker", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.appVersionIos")}
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.appVersionIos.placeholder")}
            inputValue={settingsProperties.appVersionIos}
            setInputValue={(value) =>
              handleChangeSettings("appVersionIos", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.appVersionAndroid")}
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.appVersionAndroid.placeholder")}
            inputValue={settingsProperties.appVersionAndroid}
            setInputValue={(value) =>
              handleChangeSettings("appVersionAndroid", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.supportPhoneNumber")}
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.phoneNumber.placeholder")}
            inputValue={settingsProperties.supportPhoneNumber}
            setInputValue={(value) =>
              handleChangeSettings("supportPhoneNumber", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.googleStoreLink")}
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.links.placeholder")}
            inputValue={settingsProperties.googleStoreLink}
            setInputValue={(value) =>
              handleChangeSettings("googleStoreLink", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>

        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.appleStoreLink")}
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.links.placeholder")}
            inputValue={settingsProperties.appleStoreLink}
            setInputValue={(value) =>
              handleChangeSettings("appleStoreLink", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>

        <Style.SettingElement>
          <Style.SettingTitle>
            Stripe Api Key
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.links.placeholder")}
            inputValue={settingsProperties.stripeApiKey}
            setInputValue={(value) =>
              handleChangeSettings("stripeApiKey", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>

        <Style.SettingElement>
          <Style.SettingTitle>
            Veriff Public Key
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.links.placeholder")}
            inputValue={settingsProperties.veriffPublicKey}
            setInputValue={(value) =>
              handleChangeSettings("veriffPublicKey", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>

        <Style.SettingElement>
          <Style.SettingTitle>
            Veriff Secret Key
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.links.placeholder")}
            inputValue={settingsProperties.veriffSecretKey}
            setInputValue={(value) =>
              handleChangeSettings("veriffSecretKey", value)
            }
             type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>

        <Style.SettingElement>
          <Style.SettingTitle>
            Tutorial Video Url
          </Style.SettingTitle>
          <InputAtom
            placeholder={t("page.settings.links.placeholder")}
            inputValue={settingsProperties.tutorialVideoUrl}
            setInputValue={(value) =>
              handleChangeSettings("tutorialVideoUrl", value)
            }
            type={InputTypes.MODAL_INPUT}
          />
        </Style.SettingElement>

      </Style.SettingsContent>
      <Style.Subtitle>
        {t("page.settings.subtitle.StatusAndWarningSettings")}
      </Style.Subtitle>
      <Style.StatusSettingsContent>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.maintenanceStatus")}
          </Style.SettingTitle>
          <FormGroup>
            <FormControlLabel
              disabled={settingsProperties.vacationMode ? true : false}
              control={
                <Style.ToggleSwitch
                  color="default"
                  onChange={() =>
                    handleChangeSettings(
                      "maintenanceMode",
                      !settingsProperties.maintenanceMode
                    )
                  }
                  checked={settingsProperties.maintenanceMode ? true : false}
                  colorValue={
                    settingsProperties.maintenanceMode
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                />
              }
              label={
                <Style.LabelContainer
                  colorValue={
                    settingsProperties.maintenanceMode
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                >
                  {settingsProperties.maintenanceMode ? "ON" : "OFF"}
                </Style.LabelContainer>
              }
            />
          </FormGroup>
        </Style.SettingElement>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.vacationStatus")}
          </Style.SettingTitle>
          <FormGroup>
            <FormControlLabel
              disabled={settingsProperties.maintenanceMode ? true : false}
              control={
                <Style.ToggleSwitch
                  color="default"
                  onChange={() =>
                    handleChangeSettings(
                      "vacationMode",
                      !settingsProperties.vacationMode
                    )
                  }
                  checked={settingsProperties.vacationMode ? true : false}
                  colorValue={
                    settingsProperties.vacationMode
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                />
              }
              label={
                <Style.LabelContainer
                  colorValue={
                    settingsProperties.vacationMode
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                >
                  {settingsProperties.vacationMode ? "ON" : "OFF"}
                </Style.LabelContainer>
              }
            />
          </FormGroup>
        </Style.SettingElement>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.badWeatherMode")}
          </Style.SettingTitle>
          <FormGroup>
            <FormControlLabel
              disabled={
                settingsProperties.maintenanceMode ||
                  settingsProperties.vacationMode ||
                  settingsProperties.badWeatherWarning
                  ? true
                  : false
              }
              control={
                <Style.ToggleSwitch
                  color="default"
                  onChange={() =>
                    handleChangeSettings(
                      "badWeatherMode",
                      !settingsProperties.badWeatherMode
                    )
                  }
                  checked={settingsProperties.badWeatherMode ? true : false}
                  colorValue={
                    settingsProperties.badWeatherMode
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                />
              }
              label={
                <Style.LabelContainer
                  colorValue={
                    settingsProperties.badWeatherMode
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                >
                  {settingsProperties.badWeatherMode ? "ON" : "OFF"}
                </Style.LabelContainer>
              }
            />
          </FormGroup>
        </Style.SettingElement>
        <Style.SettingElement>
          <Style.SettingTitle>
            {t("page.settings.badWeatherWarning")}
          </Style.SettingTitle>
          <FormGroup>
            <FormControlLabel
              disabled={
                settingsProperties.maintenanceMode ||
                  settingsProperties.vacationMode ||
                  settingsProperties.badWeatherMode
                  ? true
                  : false
              }
              control={
                <Style.ToggleSwitch
                  color="default"
                  onChange={() =>
                    handleChangeSettings(
                      "badWeatherWarning",
                      !settingsProperties.badWeatherWarning
                    )
                  }
                  checked={settingsProperties.badWeatherWarning ? true : false}
                  colorValue={
                    settingsProperties.badWeatherWarning
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                />
              }
              label={
                <Style.LabelContainer
                  colorValue={
                    settingsProperties.badWeatherWarning
                      ? COLORS.rainbowIndigo
                      : COLORS.terraCotta
                  }
                >
                  {settingsProperties.badWeatherWarning ? "ON" : "OFF"}
                </Style.LabelContainer>
              }
            />
          </FormGroup>
        </Style.SettingElement>
      </Style.StatusSettingsContent>
      <Style.ButtonContainer>
        <ButtonAtom
          text={t("page.settings.button.save")}
          type={ButtonTypes.MAIN_BUTTON}
          onClick={() => handleUpdateSettings()}
          width="260px"
        />
      </Style.ButtonContainer>
    </Style.Container>
  );
};

export default Settings;
